import { cva } from 'class-variance-authority';

import { Text } from '@/components/typography';
import { isLowLuminance, stringToColor } from '@/helper/colorHelper';
import { stringInitials } from '@/helper/string-util';
import { cn } from '@/lib/utils';

const letteredAvatarVariants = cva('flex select-none items-center justify-center font-normal', {
  variants: {
    size: {
      xsmall: 'size-3.5 text-[9px]',
      small: 'size-5 text-[11px]',
      medium: 'size-6 text-xs',
      large: 'size-8 text-base font-light',
      xlarge: 'size-10 text-xl font-light',
    },
    shape: {
      circle: 'rounded-full',
      square: '',
    },
    textTone: {
      light: 'text-white',
      dark: 'text-gray-900',
    },
    disabled: {
      true: 'bg-gray-300! cursor-not-allowed',
      false: '',
    },
  },
  compoundVariants: [
    { shape: 'square', size: 'xsmall', class: 'rounded-sm' },
    { shape: 'square', size: 'small', class: 'rounded-sm' },
    { shape: 'square', size: 'medium', class: 'rounded-sm' },
    { shape: 'square', size: 'large', class: 'rounded-md' },
    { shape: 'square', size: 'xlarge', class: 'rounded-md' },
  ],
  defaultVariants: {
    size: 'small',
    shape: 'circle',
    textTone: 'dark',
    disabled: false,
  },
});

type Props = {
  className?: string;
  name?: string;
  size?: 'xsmall' | 'small' | 'medium' | 'large' | 'xlarge';
  shape?: 'circle' | 'square';
  color?: string;
  disabled?: boolean;
};

const LetteredAvatar = ({
  className,
  name,
  size = 'small',
  shape = 'circle',
  color,
  disabled,
}: Props) => {
  const backgroundColor = color ?? stringToColor(name);
  const textTone = disabled || isLowLuminance(backgroundColor) ? 'light' : 'dark';

  const props = {
    className: cn(
      letteredAvatarVariants({
        size,
        shape,
        textTone,
        disabled: !!disabled,
      }),
      className,
    ),
    style: { backgroundColor: backgroundColor },
    children: stringInitials(name),
  };

  if (size === 'small' || size === 'medium') {
    return <Text variant="caption2" {...props} />;
  }

  return <Text variant="headline" {...props} />;
};

export default LetteredAvatar;
